import { HttpClient } from '@/shared';
import {
  pageQueryString,
  toPagedItems,
  type PagedItems,
  type PageQuery,
} from '@/shared/application/pagination/pagedItems';
import { X_API_KEY } from '@/shared/infra/http/runtimeEnv';
import { getEnvironmentConfig } from '@/platform/application/config/environment';
import {
  DigitizeParams,
  IStorageRepository,
  UploadStorageFileParams,
} from '../../application/ports/outbound/IStorageRepository';
import {
  AccessLogEntry,
  StorageFile,
  StreamEvent,
  UploadLocationsView,
} from '../../application/domain/entities/StorageFile';

/**
 * Calls the core-service i9-storage endpoints. CRUD/list uses the shared axios HttpClient; the
 * multipart upload and the NDJSON-streaming digitize use native fetch with `credentials: 'include'`
 * (the axios wrapper presets a JSON content-type and buffers the body — unsuitable for multipart /
 * incremental streaming), mirroring ApiBatchProcessingRepository.
 */
export class ApiStorageRepository implements IStorageRepository {
  private readonly baseUrl: string;

  constructor(private readonly httpClient: HttpClient) {
    this.baseUrl = getEnvironmentConfig().apiBaseUrlCore.replace(/\/$/, '');
  }

  private apiKeyHeaders(): Record<string, string> {
    return { 'X-Api-Key': X_API_KEY };
  }

  async listFiles(page: PageQuery): Promise<PagedItems<StorageFile>> {
    const res = await this.httpClient.get<PagedItems<StorageFile> | StorageFile[]>(
      `/i9-storage/files?${pageQueryString(page)}`
    );
    return toPagedItems(res.data, page);
  }

  async uploadFile({ file, location, locationId }: UploadStorageFileParams): Promise<StorageFile> {
    const form = new FormData();
    form.append('file', file);
    if (location) form.append('location', location);
    if (locationId) form.append('locationId', locationId);

    const res = await fetch(`${this.baseUrl}/i9-storage/files`, {
      method: 'POST',
      body: form,
      credentials: 'include',
      headers: this.apiKeyHeaders(),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Upload failed: HTTP ${res.status}`);
    }
    const json = await res.json();
    return (json?.data ?? json) as StorageFile;
  }

  async deleteFile(fileId: string): Promise<void> {
    await this.httpClient.delete(`/i9-storage/files/${fileId}`);
  }

  async viewFile(fileId: string): Promise<{ blob: Blob; contentType: string }> {
    const res = await fetch(`${this.baseUrl}/i9-storage/files/${fileId}/view`, {
      method: 'GET',
      credentials: 'include',
      headers: this.apiKeyHeaders(),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `View failed: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const contentType = res.headers.get('content-type') ?? blob.type ?? 'application/octet-stream';
    return { blob, contentType };
  }

  async getAccessLog(fileId: string): Promise<AccessLogEntry[]> {
    const res = await this.httpClient.get<AccessLogEntry[]>(
      `/i9-storage/files/${fileId}/access-log`
    );
    return res.data ?? [];
  }

  async getUploadLocations(): Promise<UploadLocationsView> {
    const res = await this.httpClient.get<UploadLocationsView>(
      '/i9-storage/files/upload-locations'
    );
    return res.data ?? { locations: [], selectionRequired: false };
  }

  async downloadFile(fileId: string, filename: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/i9-storage/files/${fileId}/download`, {
      method: 'GET',
      credentials: 'include',
      headers: this.apiKeyHeaders(),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Download failed: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async digitize(
    fileId: string,
    params: DigitizeParams,
    onEvent: (e: StreamEvent) => void
  ): Promise<void> {
    const res = await fetch(`${this.baseUrl}/i9-storage/files/${fileId}/digitize`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...this.apiKeyHeaders() },
      body: JSON.stringify({
        model: params.model,
        includeRemediation: params.includeRemediation ?? false,
      }),
    });
    await this.readNdjson(res, onEvent);
  }

  /** Read a fetch Response body as newline-delimited JSON (mirrors ApiBatchProcessingRepository). */
  private async readNdjson(res: Response, onEvent: (e: StreamEvent) => void): Promise<void> {
    if (!res.body) throw new Error(`Stream failed: HTTP ${res.status}`);
    if (!res.ok && res.status >= 400) {
      const text = await res.text().catch(() => '');
      onEvent({ type: 'error', message: text || `HTTP ${res.status}` });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          onEvent(JSON.parse(trimmed) as StreamEvent);
        } catch {
          /* ignore partial / keepalive lines */
        }
      }
    }
    const tail = buffer.trim();
    if (tail) {
      try {
        onEvent(JSON.parse(tail) as StreamEvent);
      } catch {
        /* ignore */
      }
    }
  }
}
